<template>
  <div ref="wrapperRef" class="phrase-library-wrapper">
    <slot
      :on-input="handleInput"
      :on-keydown="handleKeydown"
      :text-area-ref="setTextAreaRef"
    />

    <div
      v-if="highlightPosition.show"
      class="shorthand-highlight"
      :style="highlightPosition.style"
    ></div>

    <teleport to="body">
      <div
        v-if="showModal && searchResults.length > 0"
        data-test="phrase-library-suggestion-modal"
        class="phrase-modal"
        :class="{ 'phrase-modal--above': modalFlippedAbove }"
        :style="modalStyle"
      >
        <div class="phrase-modal-content">
          <div class="phrase-list">
            <div
              v-for="phrase in displayedResults"
              :key="phrase.id"
              :data-test="`phrase-library-suggestion-${phrase.shorthand}`"
              class="phrase-list-item"
              :class="{ active: phrase.id === activePhraseId }"
              @click="selectPhrase(phrase)"
            >
              <div
                v-dompurify-html="markMatchingChars(phrase.shorthand)"
                class="phrase-shortcut-text"
              ></div>
              <div class="phrase-expanded-text">
                {{
                  _.truncate(phrase.expanded, {
                    length: expandedMaxLength,
                    omission: "...",
                  })
                }}
              </div>
              <div
                v-if="phrase.id === activePhraseId"
                class="phrase-list-item-content"
              >
                <div>Press tab to insert</div>
                <img
                  class="phrase-tab-icon"
                  src="/images/tab-icon.svg"
                  alt="Tab icon"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </teleport>

    <div ref="mirrorRef" class="textarea-mirror"></div>
  </div>
</template>

<script setup lang="ts">
import { getWordAtCursor } from "../../../../Utils/phraselibrary";
import {
  ref,
  reactive,
  nextTick,
  onMounted,
  onUnmounted,
  computed,
  watch,
} from "vue";
import { useStore } from "vuex";
import _ from "lodash";

// --- Props ---
const props = defineProps({
  scrollContainerSelector: {
    type: String,
    default: ".main-wrapper__content",
  },
  chatTextareaRef: {
    type: Object,
    default: null,
  },
});

// --- Emits ---
const emit = defineEmits(["phrase-selected"]);

// --- Refs ---
const wrapperRef = ref(null);
const textAreaRef = ref(null);
const mirrorRef = ref<HTMLDivElement | null>(null);
const mainWrapperContentRef = ref<HTMLElement | null>(null);

//Vuex store
const store = useStore();
const phrases = computed(() => store.state.user.phrases);

// Reactive state
const showModal = ref(false);
const searchResults = ref([]);
const selectedIndex = ref(-1);
const currentWord = ref("");
const modalFlippedAbove = ref(false);

const minSearchLength = 2;

// When the modal flips above the caret, reverse the visible list so the
// best match sits at the bottom (closest to the caret).
const displayedResults = computed(() =>
  modalFlippedAbove.value
    ? [...searchResults.value].reverse()
    : searchResults.value,
);
const activePhraseId = computed(
  () => searchResults.value[selectedIndex.value]?.id,
);

// Track phone viewport reactively so rotating / resizing updates truncation.
const phoneViewportMql = window.matchMedia("(max-width: 640px)");
const isPhoneViewport = ref(phoneViewportMql.matches);
const handleViewportChange = (e: MediaQueryListEvent) => {
  isPhoneViewport.value = e.matches;
};
const expandedMaxLength = computed(() => (isPhoneViewport.value ? 32 : 64));

const highlightPosition = reactive({
  show: false,
  style: {
    position: "absolute",
    top: "0px",
    left: "0px",
    width: "0px",
    height: "0px",
    zIndex: 1,
  },
});

const modalPosition = reactive({
  position: "absolute",
  top: "0px",
  left: "0px",
});

// Chat needs z-index 100 to clear the mobile chat panel (z-index 2).
// Documentation stays at 1 so the modal doesn't paint over the page
// header when its container scrolls the textarea out of view.
const modalStyle = computed(() => ({
  ...modalPosition,
  zIndex: props.chatTextareaRef ? 100 : 1,
}));

// --- Lifecycle hooks ---
let resizeObserver: ResizeObserver | null = null;
let examDocContainer: Element | null = null;

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("resize", handleResize);
  window.addEventListener("scroll", handleScroll);
  window.visualViewport?.addEventListener("resize", repositionModalIfNeeded);
  phoneViewportMql.addEventListener("change", handleViewportChange);

  findAndAttachToMainWrapper(props.scrollContainerSelector);

  examDocContainer = document.querySelector(
    ".examination-documentation-container",
  );
  if (examDocContainer) {
    examDocContainer.addEventListener("scroll", repositionModalIfNeeded);
  }

  document.body?.addEventListener("scroll", repositionModalIfNeeded);
  document.documentElement?.addEventListener("scroll", repositionModalIfNeeded);

  if (textAreaRef.value && mirrorRef.value) {
    resizeObserver = new ResizeObserver(() => {
      copyTextAreaStylesToMirror(textAreaRef.value!, mirrorRef.value!);
      repositionModalIfNeeded();
    });
    resizeObserver.observe(textAreaRef.value);
  }
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("resize", handleResize);
  window.removeEventListener("scroll", handleScroll);
  window.visualViewport?.removeEventListener("resize", repositionModalIfNeeded);
  phoneViewportMql.removeEventListener("change", handleViewportChange);

  if (mainWrapperContentRef.value) {
    mainWrapperContentRef.value.removeEventListener(
      "scroll",
      handleMainWrapperScroll,
    );
  }

  if (examDocContainer) {
    examDocContainer.removeEventListener("scroll", repositionModalIfNeeded);
  }

  document.body?.removeEventListener("scroll", repositionModalIfNeeded);
  document.documentElement?.removeEventListener(
    "scroll",
    repositionModalIfNeeded,
  );

  resizeObserver?.disconnect();
});

// --- Event Handlers ---
const handleInput = (event: Event) => {
  const textarea = event.target as HTMLTextAreaElement;
  const cursorPos = textarea.selectionStart;
  const text = textarea.value;

  const { word: wordAtCursor, start: wordStart } = getWordAtCursor(
    text,
    cursorPos,
  );
  currentWord.value = wordAtCursor;

  if (wordAtCursor.length >= minSearchLength) {
    const found = (phrases.value || []).filter((p) =>
      p.shorthand.toLowerCase().includes(wordAtCursor.toLowerCase()),
    );

    searchResults.value = found;
    showModal.value = found.length > 0;
    highlightPosition.show = found.length > 0;

    selectedIndex.value = found.length > 0 ? 0 : -1;

    if (found.length > 0) {
      // requestAnimationFrame defers past Vue's nextTick microtasks, so
      // ResizeTextarea's transient `height: auto` measurement has settled
      // back to the real height before we read textareaRect.
      requestAnimationFrame(() => {
        positionModal(textarea, wordStart);
        positionHighlight(textarea, wordStart, wordStart + wordAtCursor.length);
      });
    }
  } else {
    resetModal();
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!showModal.value || searchResults.value.length === 0) return;

  switch (event.key) {
    case "Tab": {
      event.preventDefault();
      const phraseToSelect =
        selectedIndex.value >= 0
          ? searchResults.value[selectedIndex.value]
          : searchResults.value[0];
      if (phraseToSelect) {
        selectPhrase(phraseToSelect);
      }
      break;
    }
    case "ArrowDown": {
      event.preventDefault();
      selectedIndex.value =
        selectedIndex.value < searchResults.value.length - 1
          ? selectedIndex.value + 1
          : 0; // wrap to first item
      break;
    }
    case "ArrowUp": {
      event.preventDefault();
      selectedIndex.value =
        selectedIndex.value > 0
          ? selectedIndex.value - 1
          : searchResults.value.length - 1;
      break;
    }
    case "Escape": {
      resetModal();
      break;
    }
  }
};

const handleClickOutside = (event) => {
  if (
    !wrapperRef.value?.contains(event.target) &&
    !event.target.closest(".phrase-modal")
  ) {
    resetModal();
  }
};

// --- Modal and Highlight Management ---
const resetModal = () => {
  showModal.value = false;
  searchResults.value = [];
  selectedIndex.value = -1;
  currentWord.value = "";
  highlightPosition.show = false;
  modalFlippedAbove.value = false;
};

const selectPhrase = (phrase: any) => {
  emit("phrase-selected", phrase);
  resetModal();
};

// Soft-keyboard heuristic — visualViewport shrinks beyond the typical
// browser-UI delta (URL bar etc.) when the keyboard is up.
const isKeyboardOpen = (): boolean => {
  const vv = window.visualViewport;
  return !!vv && window.innerHeight - vv.height > 150;
};

watch(
  () => props.chatTextareaRef,
  (newEl) => {
    textAreaRef.value = newEl || null;
  },
  { immediate: true },
);

// Chat path: prop arrives after mount, so onMounted's observer guard misses it.
watch(textAreaRef, (newEl, oldEl) => {
  if (oldEl) resizeObserver?.unobserve(oldEl);
  if (!newEl || !mirrorRef.value) return;
  if (!resizeObserver) {
    resizeObserver = new ResizeObserver(() => {
      if (textAreaRef.value && mirrorRef.value) {
        copyTextAreaStylesToMirror(textAreaRef.value, mirrorRef.value);
      }
      repositionModalIfNeeded();
    });
  }
  resizeObserver.observe(newEl);
});

const setTextAreaRef = (el) => {
  if (!props.chatTextareaRef) {
    textAreaRef.value = el;
  }
};

const positionModal = (textarea: HTMLTextAreaElement, cursorPos: number) => {
  if (!textarea || !mirrorRef.value) return;

  const mirror = mirrorRef.value;
  copyTextAreaStylesToMirror(textarea, mirror);

  const textBefore = textarea.value.substring(0, cursorPos);
  const textAfter = textarea.value.substring(cursorPos);
  // Trailing `\n` before an inline span collapses as whitespace; `<br>` forces the break.
  mirror.innerHTML =
    textBefore.replace(/\n$/, "<br>") +
    `<span id="caret-marker">|</span>` +
    textAfter;

  const marker = mirror.querySelector("#caret-marker");
  if (!marker) return;

  const markerRect = marker.getBoundingClientRect();
  const mirrorRect = mirror.getBoundingClientRect();
  const textareaRect = textarea.getBoundingClientRect();

  const caretViewportLeft =
    textareaRect.left +
    (markerRect.left - mirrorRect.left) -
    textarea.scrollLeft;
  const caretViewportTop =
    textareaRect.top + (markerRect.top - mirrorRect.top) - textarea.scrollTop;
  const caretViewportBottom =
    textareaRect.top +
    (markerRect.bottom - mirrorRect.top) -
    textarea.scrollTop;

  const docLeft = caretViewportLeft + window.scrollX;
  const docBottom = caretViewportBottom + window.scrollY;

  const modalEl = document.querySelector(".phrase-modal") as HTMLElement;
  const modalWidth = modalEl?.offsetWidth || 288;
  // First-render fallback before the modal exists in the DOM.
  const modalHeight = modalEl?.offsetHeight || 200;
  const viewportWidth = window.innerWidth;
  const margin = props.chatTextareaRef ? 12 : 30;

  let leftPos = docLeft;

  // clamp so modal never leaves viewport
  leftPos = Math.min(
    leftPos,
    window.scrollX + viewportWidth - modalWidth - margin,
  );
  leftPos = Math.max(leftPos, window.scrollX + margin);

  // Chat always flips above; docs never flips.
  const flipAbove = !!props.chatTextareaRef;
  modalFlippedAbove.value = flipAbove;

  const topPos = flipAbove
    ? caretViewportTop - modalHeight - 10 + window.scrollY
    : docBottom + 10;

  modalPosition.top = `${topPos}px`;
  modalPosition.left = `${leftPos}px`;

  const arrowPaddingX = props.chatTextareaRef ? 18 : 24;

  const arrowOffset = docLeft - leftPos + arrowPaddingX;
  modalPosition["--arrow-left"] = `${arrowOffset}px`;
};

const positionHighlight = (
  textarea: HTMLTextAreaElement,
  wordStart: number,
  wordEnd: number,
) => {
  if (!textarea || !mirrorRef.value || !wrapperRef.value) return;

  const mirror = mirrorRef.value;
  const wrapper = wrapperRef.value;

  copyTextAreaStylesToMirror(textarea, mirror);

  const textBefore = textarea.value.substring(0, wordStart);
  const wordText = textarea.value.substring(wordStart, wordEnd);
  const textAfter = textarea.value.substring(wordEnd);

  mirror.innerHTML =
    textBefore.replace(/\n$/, "<br>") +
    `<span id="highlight">${wordText}</span>` +
    textAfter;

  const highlightEl = mirror.querySelector("#highlight");
  if (!highlightEl) return;

  const rects = highlightEl.getClientRects();
  if (!rects.length) return;

  const rect = rects[0];
  const wrapperRect = wrapper.getBoundingClientRect();

  const textareaScrollTop = textarea.scrollTop;
  const textareaScrollLeft = textarea.scrollLeft;

  highlightPosition.style.top = `${rect.top - wrapperRect.top - textareaScrollTop}px`;
  highlightPosition.style.left = `${rect.left - wrapperRect.left - textareaScrollLeft}px`;
  highlightPosition.style.width = `${rect.width}px`;
  highlightPosition.style.height = `${rect.height}px`;
  highlightPosition.show = true;
};

// --- Utility Functions ---
const copyTextAreaStylesToMirror = (
  textarea: HTMLTextAreaElement,
  mirror: HTMLDivElement,
) => {
  const styles = getComputedStyle(textarea);
  // border-box + overflow:hidden keeps the hidden mirror from triggering
  // horizontal document overflow when the textarea uses content-box sizing.
  mirror.style.cssText = `
    position: absolute;
    visibility: hidden;
    overflow: hidden;
    white-space: pre-wrap;
    word-wrap: break-word;
    font: ${styles.font};
    padding: ${styles.padding};
    border: ${styles.border};
    width: ${textarea.offsetWidth}px;
    height: ${textarea.offsetHeight}px;
    line-height: ${styles.lineHeight};
    box-sizing: border-box;
    font-size: ${styles.fontSize};
    font-family: ${styles.fontFamily};
    letter-spacing: ${styles.letterSpacing};
    text-transform: ${styles.textTransform};
    word-spacing: ${styles.wordSpacing};
    text-indent: ${styles.textIndent};
  `;
};

const markMatchingChars = (text: string) => {
  if (!currentWord.value) return text;

  const regex = new RegExp(`(${_.escapeRegExp(currentWord.value)})`, "gi");
  return text.replace(regex, '<span class="matching-phrase-chars">$1</span>');
};

const findAndAttachToMainWrapper = (scrollContainer: string) => {
  const mainWrapperElement = document.querySelector(scrollContainer);

  if (mainWrapperElement) {
    mainWrapperContentRef.value = mainWrapperElement as HTMLElement;
    mainWrapperElement.addEventListener("scroll", handleMainWrapperScroll);
  }
};

const repositionModalIfNeeded = () => {
  if (showModal.value && textAreaRef.value) {
    const cursorPos = textAreaRef.value.selectionStart;
    const { start: wordStart } = getWordAtCursor(
      textAreaRef.value.value,
      cursorPos,
    );
    nextTick(() => {
      positionModal(textAreaRef.value, wordStart);
      const { word: wordAtCursor } = getWordAtCursor(
        textAreaRef.value.value,
        cursorPos,
      );
      if (wordAtCursor.length >= minSearchLength) {
        positionHighlight(
          textAreaRef.value,
          wordStart,
          wordStart + wordAtCursor.length,
        );
      }
    });
  }
};

// Direct assignments
const handleResize = repositionModalIfNeeded;
const handleScroll = repositionModalIfNeeded;
const handleMainWrapperScroll = repositionModalIfNeeded;
</script>

<style scoped lang="scss">
@use "resources/assets/sass/abstracts/_variables.scss" as *;
.shorthand-highlight {
  position: absolute;
  border-bottom: 3px solid $dermicusBlue;
  width: 100%;
  height: 1rem;
  z-index: 998;
}

:deep(.matching-phrase-chars) {
  background-color: $dermicusBlue-300;
  font-weight: bold;
  border-radius: 2px;
  padding: 0 2px;
}

.textarea-mirror {
  position: absolute;
  top: 0;
  left: 0;
  visibility: hidden;
  white-space: pre-wrap;
  word-wrap: break-word;
  pointer-events: none;
}
</style>
